import { db } from '../../firebase';
import { ContentGroup } from '../types/contentGroup';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  where,
  query,
  getDoc,
  documentId,
  deleteField,
} from 'firebase/firestore';
import { parseContent, parseContents } from '../utils';
import { parseContentGroup, parseContentGroups } from './parser';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError, isProjectError } from '../../utils/errors';
import { CreateContentGroupDto, UpdateContentGroupDto } from '../typeDtos/contentGroup';
import { ContentGroupType } from '../types/others';
import _ from 'lodash';

export const getAllContentGroups = async ({
  category,
  pillarType,
  pillarExpertise,
  contentGroupType,
}: {
  category?: string;
  pillarType?: string;
  pillarExpertise?: string;
  contentGroupType: ContentGroupType;
}): Promise<ContentGroup[]> => {
  try {
    const constraints = [];
    constraints.push(where('type', '==', contentGroupType));
    category && constraints.push(where('category', '==', category));
    pillarType && constraints.push(where('pillarType', '==', pillarType));
    pillarExpertise && constraints.push(where('pillarExpertise', '==', pillarExpertise));

    const _query = query(collection(db, 'contentGroups'), ...constraints);
    const rawResult = await getDocs(_query);
    return await parseContentGroups(parseContents(rawResult));
  } catch (error) {
    console.log(`[CONTENT GROUPS] Error at getAllContentGroups with type ${contentGroupType}:`, error);
    return [];
  }
};

export const getCollectionsByAuthorId = async (authorId: string): Promise<ContentGroup[]> => {
  if (!authorId) {
    return [];
  }
  try {
    const _query = query(
      collection(db, 'contentGroups'),
      where('authorId', '==', authorId),
      where('type', '==', 'collection'),
    );
    const rawResult = await getDocs(_query);
    return await parseContentGroups(parseContents(rawResult));
  } catch (error) {
    console.log(`[CONTENT GROUPS] Error at getCollectionsByAuthorId with authorId ${authorId}:`, error);
    return [];
  }
};

export const getContentGroupById = async (id: string) => {
  try {
    const _query = await getDoc(doc(db, 'contentGroups', id));
    if (!_query.exists()) {
      console.log('No such document!');
      return undefined;
    }
    const result = await parseContentGroup(parseContent(_query));
    if (result && !isProjectError(result)) {
      return result as ContentGroup;
    }
    return undefined;
  } catch (error) {
    console.log(`[CONTENT GROUPS] Error at getContentGroupsById with id ${id}:`, error);
    return undefined;
  }
};

export const getContentGroupsByIds = async (ids: string[]): Promise<ContentGroup[]> => {
  if (!ids || (ids && ids.length === 0)) {
    return [];
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Promise<any>[] = [];
    _.chunk(ids, 10).forEach((t) => {
      const docs = getDocs(query(collection(db, 'contentGroups'), where(documentId(), 'in', t)));
      promises.push(docs);
    });
    const data = await Promise.all(promises);

    const results: { [key: string]: unknown } = {};
    if (data && Array.isArray(data)) {
      for (const t of data) {
        const parsedContentGroups = await parseContentGroups(parseContents(t)).then();
        parsedContentGroups.forEach((t) => {
          const key = t?.id;
          if (key) {
            results[key] = t;
          }
        });
      }
    }

    return ids
      .map((id: string) => {
        return results?.[id];
      })
      .filter((t) => t) as ContentGroup[];
  } catch (error) {
    console.log('[CONTENT GROUPS] Error at getContentGroupsByIds', error);
    return [];
  }
};
export const createContentGroup = async (
  data: CreateContentGroupDto,
): Promise<ProjectErrorType | { success: boolean; id: string }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = {
      ...data,
      createdAt: nowTs,
      updatedAt: nowTs,
    };
    const result = await addDoc(collection(db, 'contentGroups'), _data);
    return { success: true, id: result?.id };
  } catch (e) {
    console.log('[CONTENT GROUPS] Error at createContentGroup:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateContentGroup = async (
  data: UpdateContentGroupDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'contentGroups', _data?.id);
    Object.entries(_data)?.forEach(([key, value]) => {
      if (value === undefined) {
        // @ts-ignore
        _data[key] = deleteField();
      }
    });
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[CONTENT GROUPS] Error at updateContentGroup:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
